/** @file useTileTabs: keeps `shell.tiles` per puppet in localStorage and adds the tab a link opens. */
import { useEffect } from 'react';
import { EDITOR_SETTINGS } from '../../shell/editorSettings';
import { routePuppet } from '../../shell/routePuppet';
import type { EditorState, EditorStore, TileRef } from '../../state/editorTypes';
import { sameTile } from '../../state/sameTile';

/** Stored tabs that still have the TileRef shape (a hand-edited or old value is dropped entry by entry). */
const valid = (tiles: unknown): TileRef[] => (Array.isArray(tiles) ? tiles : []).filter((t): t is TileRef => {
  const r = t as Partial<TileRef> | null;
  return typeof r?.drawable === 'string' && Number.isInteger(r.entry) && Number.isInteger(r.tile);
});

/**
 * When the route names a puppet other than the last one, its stored tabs (`pose3d.tiles.<puppet>`) replace
 * `shell.tiles`; a `tile` route whose tab is missing (a pasted link) adds it at the end; every change of the list is
 * saved for the route's puppet.
 */
export function useTileTabs(store: EditorStore): void {
  useEffect(() => {
    let owner: string | null = null;
    const sync = (s: EditorState): void => {
      const puppetId = routePuppet(s.route);
      if (puppetId !== null && puppetId !== owner) {
        owner = puppetId;
        store.getState().setShell({ tiles: valid(EDITOR_SETTINGS.tiles(puppetId).load()) });
        return;
      }
      const route = s.route;
      if (route.screen === 'tile' && !s.shell.tiles.some((t) => sameTile(t, route.tile))) {
        store.getState().setShell({ tiles: [...s.shell.tiles, route.tile] });
        return;
      }
      if (owner !== null) EDITOR_SETTINGS.tiles(owner).save(s.shell.tiles);
    };
    sync(store.getState());
    return store.subscribe((s, prev) => { if (s.route !== prev.route || s.shell.tiles !== prev.shell.tiles) sync(s); });
  }, [store]);
}
